import { cloneDeep, max, sumBy } from 'lodash'
import { Box } from '@material-ui/core'
import {
  AllowedSeriesId,
  ComputedDatum,
  LineCanvasLayer,
  LineCustomCanvasLayer,
  LineSeries,
  PointOrSliceMouseHandler,
  ResponsiveLineCanvas,
} from '@nivo/line'
import React from 'react'
import { FullFormatOptions } from '../../../../util/formatters'
import { Tooltip } from '../../../common/charts/Tooltip'
import {
  COLOR_SCHEME_SET1,
  COLOR_SCHEME_SET1_PLUS1,
  CustomSeries,
} from '../chart-util'

type Props = {
  data: CustomSeries[]
  xFormatter: (value: number) => string
  yFormatter: (value: number, options?: FullFormatOptions) => string
  // xAxisFormatter: (value: number) => string
  axisBottomLegend?: string
  axisLeftLegend?: string
  showPoints: boolean
  legendItemWidth?: number
  onClick?: PointOrSliceMouseHandler<LineSeries>
}

export const calcMaxY = (series: CustomSeries[]): number => {
  const referenceSerie = series[0].data

  const nonStackedSeries = series.filter((s) => !s.stacked)
  const stackedSeries = series.filter((s) => s.stacked)

  const hasStackedSeries = stackedSeries.length > 0

  let nonStackedMaxY = 0
  nonStackedSeries.forEach((s) => {
    const serieMaxY = max(s.data.map((d) => Number(d.y))) ?? 0
    nonStackedMaxY = Math.max(serieMaxY, nonStackedMaxY)
  })

  const stackedSums = hasStackedSeries
    ? referenceSerie.map((_, index) => {
        const sum = sumBy(stackedSeries, (item) =>
          Number(item.data[index]?.y ?? 0),
        )
        return sum
      })
    : [0]

  const maxY = Math.max(nonStackedMaxY, max(stackedSums) ?? 0)

  return maxY
}

const ToLineSeries = (
  id: AllowedSeriesId,
  datum: readonly ComputedDatum<CustomSeries>[],
): LineSeries => {
  const tempSerie: LineSeries = { id, data: [] }
  tempSerie.data = datum.map((i) => ({
    x: i.data.x,
    y: i.data.y,
  }))

  return tempSerie
}

const CustomLines: LineCustomCanvasLayer<CustomSeries> = (ctx, props) => {
  const { series, lineGenerator, areaGenerator, xScale, yScale } = props
  series.forEach(
    ({
      id,
      data,
      color,
      lineType,
      lineWidth,
      lineColor,
      areaFill,
      areaFillColor,
      stacked,
    }) => {
      if (stacked) return

      const paths: LineSeries[] = []
      let dataCopy = cloneDeep(data)
      let index = dataCopy.findIndex((i) => i.data.y === null)

      while (index !== -1 && index < dataCopy.length) {
        if (index === 0) dataCopy = dataCopy.slice(1)
        else {
          const splittedData = dataCopy.slice(0, index)

          paths.push(ToLineSeries(id, splittedData))

          dataCopy = dataCopy.slice(index + 1)
        }

        index = dataCopy.findIndex((i) => i.data.y === null)
      }

      paths.push(ToLineSeries(id, dataCopy))

      paths.forEach((p) => {
        const values = p.data.map((d) => ({
          x: xScale(d.x),
          y: yScale(d.y),
        }))

        const path = lineGenerator(values)

        if (path) {
          const path2d = new Path2D(path)
          ctx.save()
          ctx.strokeStyle = lineColor ?? color
          ctx.lineWidth = lineWidth ?? 1
          ctx.setLineDash(lineType?.strokeDashArray ?? [])

          ctx.stroke(path2d)

          if (areaFill) {
            const areaPath = areaGenerator(values)

            if (areaPath) {
              const area2d = new Path2D(areaPath)
              ctx.globalAlpha = 0.15 // Adjust opacity as needed
              ctx.fillStyle = areaFillColor ?? color
              ctx.fill(area2d)
            }
          }
          ctx.restore()
        }
      })
    },
  )
}

export function CalibrationLineChart({
  data: customSeries,
  xFormatter,
  yFormatter,
  // xAxisFormatter,
  axisBottomLegend,
  axisLeftLegend,
  showPoints,
  legendItemWidth,
  onClick,
}: Props): React.ReactElement {
  const stackedSeries = customSeries.filter((s) => s.stacked)

  const hasStackedSeries = stackedSeries.length > 0

  const pointOrLineLayers: LineCanvasLayer<CustomSeries>[] = showPoints
    ? ['grid', 'points', 'legends', 'axes']
    : ['grid', CustomLines, 'legends', 'axes']

  const nonStackedLayers: LineCanvasLayer<CustomSeries>[] = hasStackedSeries
    ? [CustomLines, 'legends']
    : pointOrLineLayers

  const maxY = calcMaxY(customSeries)

  return (
    <>
      <Box display="flex" height="100%" justifyContent="flex-end">
        <Box position="relative" width="100%">
          {hasStackedSeries && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              zIndex={0}
            >
              <ResponsiveLineCanvas
                data={stackedSeries}
                isInteractive
                enableGridX={false}
                margin={{ top: 8, right: 10, bottom: 90, left: 50 }}
                enablePoints={false}
                colors={COLOR_SCHEME_SET1.slice(0, customSeries.length)}
                tooltip={(props) =>
                  Tooltip({ ...props, xFormatter, yFormatter })
                }
                onClick={onClick}
                lineWidth={0.1}
                axisBottom={{
                  legend: axisBottomLegend,
                  legendOffset: 35,
                }}
                axisLeft={{
                  // format: yFormatter,
                  legend: axisLeftLegend,
                  legendOffset: -35,
                }}
                xScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  nice: false,
                }}
                yScale={{
                  stacked: true,
                  type: 'linear',
                  max: maxY,
                }}
                enableArea
                areaOpacity={1}
                layers={['grid', 'areas', 'lines', 'axes']}
              />
            </Box>
          )}

          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={1}
          >
            <ResponsiveLineCanvas
              data={customSeries}
              isInteractive
              enableGridX={false}
              margin={{ top: 8, right: 10, bottom: 90, left: 50 }}
              enablePoints={showPoints}
              colors={COLOR_SCHEME_SET1_PLUS1.slice(0, customSeries.length)}
              tooltip={(props) => Tooltip({ ...props, xFormatter, yFormatter })}
              onClick={onClick}
              lineWidth={0.1}
              axisBottom={{
                legend: axisBottomLegend,
                legendOffset: 35,
              }}
              axisLeft={{
                // format: yFormatter,
                legend: axisLeftLegend,
                legendOffset: -35,
              }}
              xScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto',
                nice: false,
              }}
              yScale={{
                type: 'linear',
                max: maxY,
                min: hasStackedSeries ? undefined : 'auto',
              }}
              layers={nonStackedLayers}
              legends={[
                {
                  anchor: 'bottom',
                  direction: 'row',
                  itemHeight: 10,
                  itemWidth: legendItemWidth ?? 100,
                  itemsSpacing: 20,
                  translateY: 65,
                  symbolSize: 20,
                },
              ]}
            />
          </Box>
        </Box>
      </Box>
    </>
  )
}
